import { Text, View, SafeAreaView, StatusBar, StyleSheet, FlatList, TouchableOpacity, TextInput } from 'react-native';
import BackIcon from '../assets/images/back.svg';
import CloseIcon from '../assets/images/close.svg';
import SearchIcon from '../assets/images/search.svg';
import { COLORS, ROUTES } from '../constants';
import { NavigationProps } from './Home';
import { useState, createRef, useCallback, memo } from 'react';
import { useGlobalContext } from '../context/GlobalContext';
import { HymnProps, ItemProps } from '../types';
import { Icon } from '../lib/Icon';

const Item = memo(({item, onPress, selected, authors}: ItemProps) => {
  return (
    <TouchableOpacity onPress={onPress} style={[stylesItem.item]}>
      <View style={stylesItem.avatar}>
        <Text style={stylesItem.number}>{item.number}</Text>
      </View>
      <View>
        <Text style={[
          stylesItem.title,
          { color: selected ? COLORS.warning : COLORS.text }
        ]}>{item.title}</Text>
        <Text style={stylesItem.author}>{item.author?.text}</Text>
      </View>
    </TouchableOpacity>
  )
});

const stylesItem = StyleSheet.create({
  item: {
    marginBottom: 5,
    paddingHorizontal: 10,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    borderRadius: 5
  },
  avatar: {
    borderWidth: 1,
    borderColor: COLORS.borderLight,
    borderRadius: 50,
    height: 52,
    width: 52,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  number: {
    fontFamily: 'PoppinsBold',
    fontSize: 22,
    color: COLORS.primary
  },
  author: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: COLORS.textLight,
    marginTop: -5,
  },
  title: {
    fontSize: 18,
    fontFamily: 'PoppinsBold',
  },
});

function Search (props: NavigationProps) {
  const { setHymnId, authors, hymns } = useGlobalContext();
  const [selectedId, setSelectedId] = useState<number>();
  const [textSearch, setTextSearch] = useState<string>('');
  const [filters, setFilters] = useState<HymnProps[]>(hymns);

  const inputText = createRef<TextInput>();

  const textNormalize = (str: string) => {
    const value = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (value === undefined) return '';
    return value
      .trim()
      .replace(/[^a-z0-9_]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  };

  const search = (text: string) => {
    if (text.length > 0) {
      const needle = textNormalize(text);
      setTextSearch(needle);
      const filter = hymns.filter(item => textNormalize(item.title).includes(needle) || String(item.number).includes(needle));
      setFilters(filter.slice(0, 10));
    } else {
      setFilters(hymns);
    }
  }

  const clearInput = () => {
    setFilters(hymns);
    setTextSearch('');
    inputText.current?.clear();
    inputText.current?.focus();
  }

  const renderItem = useCallback(({item}: {item: HymnProps}) => {
    const selected = item.number === selectedId;

    const selectItem = () => {
      console.log('press selection', item);
      setSelectedId(item.number);
      setHymnId(item.number);
      setFilters([]);
      clearInput();
      props.navigation?.navigate(ROUTES.HOME_DRAWER);
    }

    return (
      <Item
        item={item}
        onPress={selectItem}
        selected={selected}
        authors={authors}
      />
    );
  }, []);
  
  const closeSearch = () => {
    clearInput();
    props.navigation?.goBack();
  }

  const ITEM_HEIGHT = 72;
  const keyExtractor = useCallback((item: HymnProps) => String(item.id), []);
  const getItemLayout = useCallback((_: any, index: number) => ({length: ITEM_HEIGHT, offset: ITEM_HEIGHT * index, index}), []);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar backgroundColor={COLORS.primary} />
      <View style={stylesHeader.container}>
        <View style={stylesHeader.containerButton}>
          <TouchableOpacity
            style={stylesHeader.buttonLeft}
            onPress={closeSearch}>
            <Icon><BackIcon /></Icon>
          </TouchableOpacity>
        </View>
        <View style={{ flex: 1 }}>
          <View
            style={stylesHeader.inputSearch}>
            <TextInput
              ref={inputText}
              autoFocus={true}
              style={stylesHeader.inputSearchText}
              placeholder="Buscar..."
              keyboardType="web-search" 
              onChangeText={search} />
            <Icon><SearchIcon style={stylesHeader.inputSearchIcon} /></Icon>
          </View>
        </View>
        <View style={stylesHeader.containerButton}>
          <TouchableOpacity
            style={stylesHeader.buttonRight}
            onPress={closeSearch}>
            <Icon><CloseIcon style={{ fill: COLORS.white }}/></Icon>
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.body}>
        {filters.length > 0 && <FlatList
          data={filters}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          extraData={selectedId}
          getItemLayout={getItemLayout}
          initialNumToRender={ 10 }
          windowSize={ 10 } 
          maxToRenderPerBatch={ 10 }
        />}
        {filters.length === 0 && <Text style={styles.filterEmpty}>
          {textSearch.length > 0 ? 'No existe resultados.' : 'Puede buscar por el número o nombre del himno.'}
        </Text>}
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.primary,
    flex: 1,
    position: 'relative',
  },
  body: {
    flex: 1,
    backgroundColor: COLORS.background,
    padding: 8 
  },
  image: {
    flex: 1,
    justifyContent: 'center',
  },
  filterEmpty: {
    textAlign: 'center',
    fontFamily: 'Poppins',
    marginTop: 10
  }
});

const stylesHeader = StyleSheet.create({
  container: {
    backgroundColor: COLORS.primary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 54
  },
  containerButton: {
    width: 52
  },
  buttonLeft: {
    padding: 10,
    marginTop: 5,
    marginHorizontal: 5,
    height: 40
  },
  buttonRight: {
    padding: 10,
    marginTop: 5,
    marginHorizontal: 5,
    height: 40
  },
  inputSearch: {
    backgroundColor: COLORS.white,
    height: 38,
    borderRadius: 20,
    marginTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  inputSearchText: {
    borderRadius: 20,
    color: COLORS.textLight,
    fontFamily: 'Poppins',
    fontSize: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginBottom: -2,
    flex: 1,
  },
  inputSearchIcon: {
    marginTop: 9,
    marginRight: 12
  }
});

export default Search;
